import { getImageProps } from 'next/image';

export interface ImageCandidate {
  width: number;
  /** Descritor do srcset: `828w` (com `sizes`) ou `1x`/`2x` (largura fixa). */
  descriptor: string;
}

/**
 * Larguras que o `next/image` usaria para esta imagem, com a config de imagens
 * DO PROJETO CONSUMIDOR (`images.deviceSizes`/`imageSizes` do next.config.js).
 *
 * Usa a API pública `getImageProps` (Next ≥ 14.1), que aplica o mesmo algoritmo
 * do `next/image`: largura fixa sem `sizes` → 1x e 2x, arredondados para cima
 * até um valor da lista; com `sizes` em `vw` → todas as larguras da lista a
 * partir de `deviceSizes[0] × a menor porcentagem`. O Next injeta a config no
 * bundle (`process.env.__NEXT_IMAGE_OPTS`), no servidor e no navegador — a lib
 * entra no `transpilePackages`, então enxerga a do consumidor.
 *
 * O loader passado ao `getImageProps` só devolve a largura: aqui interessa a
 * LISTA, e quem gera arquivo é o loader do consumidor, chamado depois pelo
 * componente — uma vez por largura escolhida.
 *
 * Com `images.unoptimized: true` o Next não gera srcset nenhum; a lib respeita
 * e devolve um candidato só, na largura pedida.
 */
export function imageCandidates({
  width,
  height,
  sizes,
}: {
  width: number;
  height: number;
  sizes?: string;
}): ImageCandidate[] {
  const { props } = getImageProps({
    src: '/wp-nextjs-tools/candidates',
    alt: '',
    width,
    height,
    sizes,
    loader: ({ width: w }) => String(w),
  });

  if (!props.srcSet) return [{ width: Math.round(width), descriptor: '1x' }];

  return props.srcSet.split(',').map((entry) => {
    const [w, descriptor] = entry.trim().split(/\s+/);
    return { width: Number(w), descriptor };
  });
}

/** Monta um srcset chamando `urlFor` uma vez por candidato. */
export const buildSrcSet = (candidates: ImageCandidate[], urlFor: (width: number) => string): string =>
  candidates.map(({ width, descriptor }) => `${urlFor(width)} ${descriptor}`).join(', ');

/** Dimensão do arquivo de origem, quando conhecida. */
export interface SourceDimensions {
  width: number;
  height: number;
}

/**
 * Dimensão da origem embutida no nome do arquivo (`…/abc-954x357.png`), que o
 * `changeDataByLocal` grava desde a 4.0.0. Vem do nome, e não do disco, de
 * propósito: o navegador também precisa dela para montar o MESMO srcset que o
 * build montou — senão a hidratação diverge.
 */
export const sourceDimensionsFromSrc = (src: string): SourceDimensions | undefined => {
  const m = /-(\d+)x(\d+)\.[A-Za-z0-9]+$/.exec(src);
  if (!m) return undefined;
  const width = Number(m[1]);
  const height = Number(m[2]);
  return width > 0 && height > 0 ? { width, height } : undefined;
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Corta os candidatos que passariam da origem.
 *
 * O imgproxy não amplia: pedir 1920 de uma origem de 954 devolve 954 — com o
 * nome de 1920. O `next/image` não faz este corte porque não conhece a origem;
 * a lib conhece. Sem ele, cada largura acima da origem virava uma cópia do
 * mesmo arquivo com outro nome (no `wp-cases`, 451 de 624 arquivos), e o
 * srcset anunciava um `2x` que não era 2x.
 *
 * - Descritor `w`: ficam as larguras até `maxWidth`, e entra o próprio
 *   `maxWidth` como último candidato, com o descritor verdadeiro.
 * - Descritor `x` (largura fixa): se só o 2x passa, ele vira a origem com a
 *   densidade real (ex.: `1.6x`). Se nem o 1x cabe, a origem é o único
 *   candidato, **com descritor `w`** (`458w`) — e o componente emite
 *   `sizes="<width>px"`. Nem `1x` nem `0.77x` servem: com `1x`, a largura
 *   intrínseca do `<img>` vira a do arquivo (458) em vez da declarada (595), e
 *   a imagem encolhe num container que se ajusta ao conteúdo; com `0.77x`, o
 *   navegador acrescenta o `src` como candidato `1x` implícito (a spec faz isso
 *   quando não há `1x` nem descritor `w`) e escolhe ele — dá 458 do mesmo jeito.
 *   Com `458w` + `sizes="595px"`, o `src` não entra e a largura intrínseca é a
 *   do slot, 595 (conferido no Chrome, inclusive com `width: auto` no CSS).
 *
 * `maxWidth` é a maior largura que sai sem ampliar — a da origem, ou menos,
 * quando há recorte numa proporção mais larga que ela.
 */
export function capCandidates(
  candidates: ImageCandidate[],
  { displayWidth, maxWidth }: { displayWidth: number; maxWidth?: number }
): ImageCandidate[] {
  if (!maxWidth || maxWidth <= 0 || candidates.length === 0) return candidates;
  const max = Math.floor(maxWidth);
  const kept = candidates.filter((c) => c.width <= max);
  if (kept.length === candidates.length) return candidates;

  const isDensity = candidates[0].descriptor.endsWith('x');
  if (isDensity) {
    if (kept.length === 0) return [{ width: max, descriptor: `${max}w` }];
    const density = round2(max / displayWidth);
    const last = kept[kept.length - 1];
    if (max === last.width || density <= parseFloat(last.descriptor)) return kept;
    return [...kept, { width: max, descriptor: `${density}x` }];
  }

  const last = kept[kept.length - 1];
  if (last && last.width === max) return kept;
  return [...kept, { width: max, descriptor: `${max}w` }];
}
